import { Component, memo, ReactNode, useCallback, useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ButtonTertiary } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import * as Icons from "@nova/component-icons"
import { JobStatusType, SatelliteSensorType, ToolName, zIndex } from "@nova/constants"
import { DateFormat, formatArea, formatDate } from "@nova/format"
import { config } from "@nova/configuration/browser"
import { calculateArea } from "@nova/calculate-geospatial"
import { useDebounceDispatch } from "tools/store"
import { selectSatelliteFlightById } from "features/entity-caching/flights/selectors"
import { selectWorkspaceAreaUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectOrthoOpacityById, setOrthoOpacity } from "plugins/Orthomosaic/OrthomosaicSlice"
import {
    activateOrthoComparison,
    type OrthoComparisonBounds,
} from "plugins/Orthomosaic/OrthoComparisonSlice"
import { startMaskDrawing } from "plugins/Orthomosaic/OrthoMaskSlice"
import { selectOrthoMaskByFlightId } from "features/entity-caching/ortho-masks/selectors"
import { updateOrthoMask } from "features/pubsub/entityActions/cubes/orthoMask"
import { updateSatelliteFlight } from "features/pubsub/entityActions/flights/satellite"
import { setActiveTool } from "plugins/Toolbox/tool-registry"
import {
    selectSatelliteOverlapIsVisible,
    setSatelliteOverlapVisibility,
    setSatelliteHoveredSceneId,
} from "plugins/Satellite/SatelliteOverlapSlice"
import { useSatelliteSceneAtCursor } from "plugins/Satellite/provenance/useSatelliteSceneAtCursor"
import { useSatelliteProvenanceData } from "plugins/Satellite/provenance/useSatelliteProvenanceData"
import {
    cogOrthoBbox,
    hasProcessingOption,
    selectActiveCogOrtho,
} from "plugins/Satellite/layers/satelliteLayer"
import { InspectorPanelAccordion } from "../accordion/InspectorPanelAccordion"
import { InspectorPanelAccordionSlider } from "../accordion/InspectorPanelAccordionSlider"
import { InspectorPanelAccordionKeyValueToggle } from "../accordion/InspectorPanelAccordionKeyValueToggle"
import { SatelliteProvenanceTimespan } from "./SatelliteProvenanceTimespan"
import { SatelliteProvenanceTooltip } from "./SatelliteProvenanceTooltip"
import { NovaSpinner } from "@nova/component-nova-spinner"

const sensorMission: Record<SatelliteSensorType, string> = {
    [SatelliteSensorType.Visual]: "Sentinel-2",
    [SatelliteSensorType.Infrared]: "Sentinel-2",
    [SatelliteSensorType.Radar]: "Sentinel-1",
}

const temporalBiasLabel: Record<string, string> = {
    before: "Before",
    after: "After",
    closest: "Closest to",
}

const filtersInfo = {
    cloudFree: {
        label: "Cloud-Free",
        description: "Stacks satellite images across multiple days to create a cloud free mosaic.",
    },
    dehaze: {
        label: "Dehaze",
        description: "Cuts through smoke and haze to see ground details otherwise obscured.",
    },
} as const

type FilterOptionKey = keyof typeof filtersInfo

function FilterOptionRow({
    control,
    label,
    isActive,
    onClick,
    onMouseEnter,
    onMouseLeave,
}: {
    control: React.ReactNode
    label: string
    isActive: boolean
    onClick: () => void
    onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void
    onMouseLeave?: () => void
}) {
    return (
        <div
            className="flex items-center gap-[11px] -mx-2 px-2 py-1 rounded-md select-none hover:bg-white-200 cursor-pointer"
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {control}
            <span className={isActive ? "text-gray-0" : "text-gray-100"}>{label}</span>
        </div>
    )
}

function InfoPanelArrow({ arrowY }: { arrowY: number }) {
    return (
        <>
            <div
                style={{
                    position: "absolute",
                    left: "100%",
                    top: arrowY,
                    width: 0,
                    height: 0,
                    borderTop: "7px solid transparent",
                    borderBottom: "7px solid transparent",
                    borderLeft: "7px solid #28282C",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: "calc(100% - 1px)",
                    top: arrowY + 1,
                    width: 0,
                    height: 0,
                    borderTop: "6px solid transparent",
                    borderBottom: "6px solid transparent",
                    borderLeft: "6px solid #0C0C0F",
                }}
            />
        </>
    )
}

type HoveredFilterOption = {
    key: FilterOptionKey
    midY: number
}

function FilterInfoPanel({ hovered, rowLeft }: { hovered: HoveredFilterOption; rowLeft: number }) {
    const info = filtersInfo[hovered.key]
    const panelW = 220
    const panelH = 90
    const gap = 36
    const left = rowLeft - gap - panelW
    const topMax = window.innerHeight - 65 - panelH
    const top = Math.max(8, Math.min(topMax, hovered.midY - panelH / 2))
    const arrowY = Math.max(10, Math.min(panelH - 22, hovered.midY - top - 6))

    return (
        <div
            className="pointer-events-none fixed bg-black-800 border border-gray-700 rounded-xl shadow-card p-4"
            style={{ left, top, width: panelW, zIndex: zIndex.PortalledTooltip }}
        >
            <InfoPanelArrow arrowY={arrowY} />
            <div className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider mb-2">
                {info.label}
            </div>
            <div className="text-gray-100 leading-relaxed" style={{ fontSize: 13 }}>
                {info.description}
            </div>
        </div>
    )
}

const InspectorPanelSatelliteLayerContent = memo(({ id }: { id: string }) => {
    const dispatch = useAppDispatch()
    const { debounceDispatch } = useDebounceDispatch(250)
    const flight = useAppSelector(selectSatelliteFlightById(id))
    const existingOpacity = useAppSelector(selectOrthoOpacityById(id))
    const orthoMask = useAppSelector(selectOrthoMaskByFlightId(id))
    const isOverlapEnabled = !!useAppSelector(selectSatelliteOverlapIsVisible(id))
    const areaUnit = useAppSelector(selectWorkspaceAreaUnit)
    const { organizationSettingDateFormat } = config.use("organizationSettingDateFormat")
    const [openCategories, setOpenCategories] = useState(new Set<string>())
    const filtersRowRef = useRef<HTMLDivElement>(null)
    const [hoveredFilterOption, setHoveredFilterOption] = useState<HoveredFilterOption | null>(null)
    // Single instance shared via redux with the map-layer-plugin hook - don't duplicate this per caller.
    const { scene: hoveredScene, cursor: hoveredCursor } = useSatelliteSceneAtCursor(id, true)
    const { dateRange } = useSatelliteProvenanceData(id)

    const setCategoryIsOpen = useCallback((category: string, isOpen: boolean) => {
        setOpenCategories((prev) => {
            const next = new Set(prev)
            if (isOpen) next.add(category)
            else next.delete(category)
            return next
        })
    }, [])

    useEffect(() => {
        dispatch(setSatelliteHoveredSceneId({ id, sceneId: hoveredScene?.sceneId }))
    }, [dispatch, id, hoveredScene?.sceneId])

    if (!flight) return null

    const isJobReady = flight.job?.status ? flight.job.status >= JobStatusType.COMPLETED : false
    const cogOrtho = selectActiveCogOrtho(flight)
    if (!isJobReady || !cogOrtho) return null

    const { minLng, maxLng, minLat, maxLat } = cogOrtho
    const hasCloudFreeToggle =
        flight.mosaic.some((m) => hasProcessingOption(m, "cloudfree")) &&
        flight.mosaic.some((m) => !hasProcessingOption(m, "cloudfree"))
    const hasDehazeToggle = flight.mosaic.some((m) => hasProcessingOption(m, "dehaze"))
    const isDehazePending =
        flight.sensorType === SatelliteSensorType.Visual &&
        !hasDehazeToggle &&
        Date.now() / 1000 - flight.createdAt < 600

    const directionLabel = flight.temporalBias ? temporalBiasLabel[flight.temporalBias] : undefined
    const formattedDate = flight.temporalAnchor
        ? formatDate({
              value: new Date(flight.temporalAnchor * 1000),
              format: organizationSettingDateFormat as DateFormat,
          })
        : undefined
    const sourceSubtitle =
        directionLabel && formattedDate ? `${directionLabel} ${formattedDate}` : undefined

    const filterOptionLabels = [flight.cloudFree && "Cloud-Free", flight.dehaze && "Dehaze"]
        .filter((label): label is string => !!label)
        .join(", ")
    const filtersSubtitle = filterOptionLabels || "None"

    const sensorType = flight.sensorType as SatelliteSensorType | undefined
    const mission = sensorType !== undefined ? sensorMission[sensorType] : undefined
    const attributionYear = (
        dateRange?.last ??
        (flight.temporalAnchor ? new Date(flight.temporalAnchor * 1000) : new Date())
    ).getFullYear()
    const attribution = mission ? { mission, year: attributionYear } : undefined
    const coverageArea = formatArea({
        value: calculateArea(cogOrthoBbox(cogOrtho)),
        unit: areaUnit,
    })

    const handleActivateComparison = () => {
        const bounds: OrthoComparisonBounds = { minLng, maxLng, minLat, maxLat }
        dispatch(
            activateOrthoComparison({
                flightId: flight.id,
                bounds,
                threshold: (minLng + maxLng) / 2,
            })
        )
    }

    return (
        <div className="flex flex-col gap-2 mb-2">
            <InspectorPanelAccordion
                title="Source"
                subtitle={sourceSubtitle}
                isOpen={openCategories.has("source")}
                setIsOpen={(isOpen) => {
                    setCategoryIsOpen("source", isOpen)
                    if (!isOpen && isOverlapEnabled)
                        dispatch(setSatelliteOverlapVisibility({ id: flight.id, active: false }))
                }}
                isToggled={isOverlapEnabled}
                setIsToggled={(active) =>
                    dispatch(setSatelliteOverlapVisibility({ id: flight.id, active }))
                }
            >
                <div className="flex flex-col gap-2 my-[2px]">
                    <span className="text-gray-0 text-[14px]">Segments</span>
                    <SatelliteProvenanceTimespan flightId={flight.id} scene={hoveredScene} />
                    {/* Attribution required by ESA Copernicus per Sentinel_Data_Legal_Notice*/}
                    {!!attribution && (
                        <small className="block font-mono text-gray-300 leading-snug py-1">
                            Copernicus {attribution.mission} {attribution.year}
                            <br />
                            Processed by Nova
                        </small>
                    )}
                </div>
            </InspectorPanelAccordion>

            {(hasCloudFreeToggle || hasDehazeToggle || isDehazePending) && (
                <InspectorPanelAccordion
                    title="Filters"
                    subtitle={filtersSubtitle}
                    isOpen={openCategories.has("filters")}
                    setIsOpen={(isOpen) => setCategoryIsOpen("filters", isOpen)}
                >
                    <div className="flex flex-col" ref={filtersRowRef}>
                        {hasCloudFreeToggle && (
                            <FilterOptionRow
                                control={
                                    <TriCheckbox
                                        state={flight.cloudFree ? "checked" : "unchecked"}
                                        size="small"
                                    />
                                }
                                label="Cloud-Free"
                                isActive={!!flight.cloudFree}
                                onClick={() =>
                                    dispatch(
                                        updateSatelliteFlight({
                                            id: flight.id,
                                            updates: { cloudFree: !flight.cloudFree },
                                        })
                                    )
                                }
                                onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect()
                                    setHoveredFilterOption({
                                        key: "cloudFree",
                                        midY: rect.top + rect.height / 2,
                                    })
                                }}
                                onMouseLeave={() => setHoveredFilterOption(null)}
                            />
                        )}
                        {hasDehazeToggle && (
                            <FilterOptionRow
                                control={
                                    <TriCheckbox
                                        state={flight.dehaze ? "checked" : "unchecked"}
                                        size="small"
                                    />
                                }
                                label="Dehaze"
                                isActive={!!flight.dehaze}
                                onClick={() =>
                                    dispatch(
                                        updateSatelliteFlight({
                                            id: flight.id,
                                            updates: { dehaze: !flight.dehaze },
                                        })
                                    )
                                }
                                onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect()
                                    setHoveredFilterOption({
                                        key: "dehaze",
                                        midY: rect.top + rect.height / 2,
                                    })
                                }}
                                onMouseLeave={() => setHoveredFilterOption(null)}
                            />
                        )}
                        {!hasDehazeToggle && isDehazePending && (
                            <div className="flex items-center gap-[11px] -mx-2 px-2 py-1">
                                <NovaSpinner
                                    width={20}
                                    height={20}
                                    className="m-[2px] text-blue-300"
                                    fill="#B1B1B3"
                                    color="#0C0C0F"
                                />
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-300">Dehaze</span>
                                    <small className="text-gray-500">Loading...</small>
                                </div>
                            </div>
                        )}
                    </div>
                </InspectorPanelAccordion>
            )}

            <InspectorPanelAccordion
                title="Satellite Mosaic"
                subtitle={coverageArea && `${coverageArea} coverage`}
                isOpen={openCategories.has("mosaic")}
                setIsOpen={(isOpen) => setCategoryIsOpen("mosaic", isOpen)}
                hasSubsections
            >
                <div className="flex flex-col px-2.5 gap-1">
                    <div className="pb-2">
                        <InspectorPanelAccordionSlider
                            title="Opacity"
                            resetValue={100}
                            existingSliderValue={existingOpacity}
                            minSliderValue={1}
                            maxSliderValue={100}
                            onValueChanged={(opacity) =>
                                debounceDispatch(
                                    setOrthoOpacity({ id: flight.id, updates: { opacity } })
                                )
                            }
                        />
                    </div>
                    {!!orthoMask && (
                        <InspectorPanelAccordionKeyValueToggle
                            keyTitle="Crop"
                            isToggled={orthoMask.enabled}
                            setIsToggled={() =>
                                dispatch(
                                    updateOrthoMask({
                                        id: orthoMask.id,
                                        updates: { enabled: !orthoMask.enabled },
                                    })
                                )
                            }
                        />
                    )}
                </div>
                <div
                    className={classNames(
                        "flex pt-2 px-2.5 w-full border-t border-t-gray-700 text-gray-50 gap-1"
                    )}
                >
                    <ButtonTertiary
                        title="Compare"
                        LeftIcon={Icons.RatioBar}
                        className="w-full"
                        onClick={handleActivateComparison}
                    />
                    <ButtonTertiary
                        title="Crop"
                        LeftIcon={Icons.Crop}
                        className="w-full"
                        onClick={() => {
                            dispatch(setActiveTool(ToolName.OrthoMask))
                            dispatch(
                                startMaskDrawing({
                                    flightId: flight.id,
                                    existingMask: orthoMask,
                                })
                            )
                        }}
                    />
                </div>
            </InspectorPanelAccordion>

            {hoveredFilterOption && filtersRowRef.current && (
                <FilterInfoPanel
                    hovered={hoveredFilterOption}
                    rowLeft={filtersRowRef.current.getBoundingClientRect().left}
                />
            )}
            {isOverlapEnabled && (
                <SatelliteProvenanceTooltip
                    flightId={flight.id}
                    sensorType={flight.sensorType}
                    scene={hoveredScene}
                    cursor={hoveredCursor}
                />
            )}
        </div>
    )
})
InspectorPanelSatelliteLayerContent.displayName = "InspectorPanelSatelliteLayerContent"

type BoundaryState = { hasError: boolean }

// Isolates failures from the external, unvalidated manifest.json to this panel instead of the whole app.
class SatellitePanelErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
    state: BoundaryState = { hasError: false }

    static getDerivedStateFromError(error: Error) {
        console.error(error)
        return { hasError: true }
    }

    render() {
        if (this.state.hasError) {
            return <div className="p-4 text-gray-100">Unable to display this layer.</div>
        }
        return this.props.children
    }
}

const InspectorPanelSatelliteLayer = ({ id }: { id: string }) => (
    <SatellitePanelErrorBoundary>
        <InspectorPanelSatelliteLayerContent id={id} />
    </SatellitePanelErrorBoundary>
)

export { InspectorPanelSatelliteLayer }
